/**
 * Durable local store — IndexedDB with a small typed promise wrapper.
 *
 * Privacy: exact birth data and full chart results live HERE, on-device.
 * The cloud only ever receives derived summaries (see docs/PRIVACY_MODEL.md).
 *
 * The interface is deliberately narrow (get/set/del/list by store) so a
 * later swap to @capacitor-community/sqlite is a drop-in change.
 */

const DB_NAME = 'astrally';
const DB_VERSION = 1;
export const STORES = [
  'kv', // settings, session, birth profile, serialized charts
  'demoUsers',
  'demoMemories',
  'demoRecommendations',
  'demoIntroductions',
  'demoConversations',
  'demoMessages',
] as const;
export type StoreName = (typeof STORES)[number];

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const store of STORES) {
        if (!db.objectStoreNames.contains(store)) db.createObjectStore(store);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const localdb = {
  async get<T>(store: StoreName, key: string): Promise<T | undefined> {
    return tx<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
  },
  async set<T>(store: StoreName, key: string, value: T): Promise<void> {
    await tx(store, 'readwrite', (s) => s.put(value as unknown as object, key));
  },
  async del(store: StoreName, key: string): Promise<void> {
    await tx(store, 'readwrite', (s) => s.delete(key));
  },
  async keys(store: StoreName): Promise<string[]> {
    const ks = await tx<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys());
    return ks.map(String);
  },
  async all<T>(store: StoreName): Promise<T[]> {
    return tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
  },
  async clear(store: StoreName): Promise<void> {
    await tx(store, 'readwrite', (s) => s.clear());
  },
  async wipeEverything(): Promise<void> {
    for (const store of STORES) await this.clear(store);
  },
};
